/*
 * Copyright (c) 2025. Bolmate B.V.
 */

import React, {useContext, useEffect, useRef, useState} from 'react';
import {sendGaPageView} from "../../util/analytics";
import {NOTIFICATIONS_ROUTE} from "../../util/constants";
import {onSetCrumbs} from "../../util/events";
import {AppContext} from "../misc/appContext";
import Subscription from "../../models/subscriptionModel";
import {SubscriptionModuleName} from "../../util/enums";
import {createErrorMessage} from "../../util/utils";
import {ResponseData, showDialog, showSnackbar} from '@dvrd/dvr-controls';
import TestMailController, {TestMailRef} from "../email/test/testMailController";
import Notification from "../../models/notificationModel";
import {sendTestNotification} from "../../util/requestSender";
import NotificationsPage from "./notificationsPage";
import { ActivateHandle } from '../../util/interfaces';
import NotificationFeedbackController from "./notificationFeedback/notificationFeedbackController";

export default function NotificationsPageController() {
    const context = useContext(AppContext);
    const {user} = context.userContext;
    const {account} = context.accountContext;
    const [loading, setLoading] = useState(false);
    const testMailRef = useRef<TestMailRef>(null);
    const feedbackRef = useRef<ActivateHandle>(null);
    const testingNotification = useRef<Notification | null>(null);

    function onActivateModule() {
        if (!account || !user?.subscription) return;
        setLoading(true);
        const subscription = user.subscription;
        subscription.addModule({accountID: account.id, moduleName: SubscriptionModuleName.NOTIFICATIONS},
            (subscription: Subscription, success: boolean, data: ResponseData) => {
                setLoading(false);
                if (success) {
                    user.subscription = subscription;
                    context.onChangeContext({user});
                    showSnackbar('De module is toegevoegd aan het abonnement');
                } else
                    showDialog(createErrorMessage(data.message ?? 'Het toevoegen van de module is niet gelukt.'),
                        'Toevoegen mislukt');
            })
    }

    function onSendTestNotification(notification: Notification) {
        return function() {
            testingNotification.current = notification;
            testMailRef.current?.activate(notification.firstEmailAddress);
        }
    }

    function onSendTestMail(email: string) {
        if (!testingNotification.current) return;
        testMailRef.current?.setLoading(true);
        const notification = testingNotification.current;
        sendTestNotification({
            data: {
                account_id: notification.accountId, email_address: email, notification_id: notification.id
            }, callback: (data: ResponseData) => {
                testMailRef.current?.setLoading(false);
                if (data.success) {
                    testMailRef.current?.onClose();
                    showSnackbar('De notificatie is verzonden');
                } else
                    showDialog(createErrorMessage(data.message ?? 'Het verzenden van de notificatie is niet gelukt.'),
                        'Verzenden mislukt');
            }
        });
    }

    function onPulseModule() {
        const button = document.getElementById('btn-activate');
        if (!button) return;
        button.addEventListener('animationend', () => {
            button.classList.remove('pulse');
        });
        button.classList.add('pulse');
    }

    function onClickFeedback() {
        feedbackRef.current?.activate();
    }

    useEffect(() => {
        sendGaPageView(NOTIFICATIONS_ROUTE);
        onSetCrumbs([{label: 'Notificaties'}]);
    }, []);

    return (
        <>
            <NotificationsPage onActivateModule={onActivateModule} onSendTestNotification={onSendTestNotification}
                               onPulseModule={onPulseModule} onClickFeedback={onClickFeedback} loading={loading}/>
            <TestMailController onSubmit={onSendTestMail} ref={testMailRef} closeOnSubmit={false}/>
            <NotificationFeedbackController ref={feedbackRef}/>
        </>
    )
}